"use client";

import { cn } from "@faire/react/tools/cn";
import { useViewport } from "@faire/react/viewport/useViewport";
import { useIsSSR } from "@faire/ssr/isSSR";
import React from "react";

export interface IProps {
  mobile?: React.ReactNode;
  tablet?: React.ReactNode;
  desktop?: React.ReactNode;
  xlarge?: React.ReactNode;
  xxlarge?: React.ReactNode;
  tabletAndAbove?: React.ReactNode;
  tabletAndBelow?: React.ReactNode;
  desktopAndAbove?: React.ReactNode;
  xLargeAndAbove?: React.ReactNode;
  noJS?: boolean;
  /**
   * Avoid adding depth to the DOM by wrapping children in <span>s in noJS-mode.
   *
   * Instead, responsive classNames will be passed to children.
   */
  flat?: boolean;
}

/**
 * This component can be used to conditionally display content based on the viewport.
 *
 * If used with the "noJS" prop, if used as a React Server Component (RSC),
 * or during SSR, it will render all possible viewports,
 * hiding ones that do not apply using media queries.
 *
 * It is not valid to pass noJS={false} if rendered from a React Server Component (RSC).
 */
export const ResponsiveContentV2: React.FC<IProps> =
  (props) => {
    const {
      mobile,
      tablet,
      desktop,
      xlarge,
      xxlarge,
      tabletAndAbove,
      tabletAndBelow,
      desktopAndAbove,
      xLargeAndAbove,
      noJS,
    } = props;
    const isSSR = useIsSSR();
    const viewport = useViewport();

    if (isSSR || noJS) {
      return <StaticResponsiveContent {...props} noJS={true} />;
    }
    if (viewport.isMobile && mobile) {
      return <>{mobile}</>;
    }
    if (viewport.isTablet && tablet) {
      return <>{tablet}</>;
    }
    if (viewport.isDesktop && desktop) {
      return <>{desktop}</>;
    }
    if (viewport.isXLargeDesktop && xlarge) {
      return <>{xlarge}</>;
    }
    if (viewport.isXXLargeDesktop && xxlarge) {
      return <>{xxlarge}</>;
    }
    if (viewport.isXLargeAndAbove && xLargeAndAbove) {
      return <>{xLargeAndAbove}</>;
    }
    if (viewport.isDesktopAndAbove && desktopAndAbove) {
      return <>{desktopAndAbove}</>;
    }
    if (viewport.isTabletAndAbove && tabletAndAbove) {
      return <>{tabletAndAbove}</>;
    }
    if (viewport.isTabletAndBelow && tabletAndBelow) {
      return <>{tabletAndBelow}</>;
    }
    return null;
  };

const StaticResponsiveContent: React.FC<IProps> = (props) => {
  const {
    mobile,
    tablet,
    desktop,
    xlarge,
    xxlarge,
    tabletAndAbove,
    tabletAndBelow,
    desktopAndAbove,
    xLargeAndAbove,
    noJS,
    flat,
  } = props;

  if (noJS === false) {
    throw new Error(
      "StaticResponsiveContent is being used as a React Server Component, but noJS was set to false."
    );
  }

  const wrap = (content: React.ReactNode, className: string) => {
    if (flat) {
      return React.Children.map(content, (child) => {
        return React.cloneElement(
          child as React.ReactElement<{ className?: string }>,
          {
            className: cn(
              className,
              (child as React.ReactElement<{ className?: string }>).props
                .className
            ),
          }
        );
      });
    } else {
      return <span className={className}>{content}</span>;
    }
  };

  // Each slot uses hide-only responsive variants (`display:none` outside its
  // range) so the element keeps its natural display when visible — important for
  // `flat` mode, where the class is cloned onto arbitrary children.
  return (
    <>
      {mobile ? wrap(mobile, "md:hidden") : null}
      {tablet ? wrap(tablet, "max-md:hidden lg:hidden") : null}
      {desktop ? wrap(desktop, "max-lg:hidden xl:hidden") : null}
      {xlarge ? wrap(xlarge, "max-xl:hidden 2xl:hidden") : null}
      {xxlarge ? wrap(xxlarge, "max-2xl:hidden") : null}
      {tabletAndAbove ? wrap(tabletAndAbove, "max-md:hidden") : null}
      {tabletAndBelow ? wrap(tabletAndBelow, "lg:hidden") : null}
      {desktopAndAbove ? wrap(desktopAndAbove, "max-lg:hidden") : null}
      {xLargeAndAbove ? wrap(xLargeAndAbove, "max-xl:hidden") : null}
    </>
  );
};
